import React from 'react'
import { Search, Filter } from 'lucide-react';
import PageHeader from '../../componentsII/PageHeader';
import api from '../../axios/api';
import { TableContainer, TableBody, TableCell, Switch, TableFooter, TableHead, TablePagination, TableRow, Table, Paper, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { useEffect, useState } from 'react';
import { X, Trash2 as DeleteIcon, PenBoxIcon, PlusIcon } from 'lucide-react';
import CustomButton from '../../componentsII/Buttons';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../redux/slices/notificationSlice';
import { TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import debounce from 'lodash/debounce';
import { format } from "date-fns";
import AddUpdateIndGasEmissionModal from '../../components/Masters/EmissionIndGasMaster/AddUpdateIndGasEmissionModal';

const EmissionIndGasMaster = () => {
  const dispatch = useDispatch();

  const [isAddUpdateIndGasEmissionModalOpen, setIsAddUpdateIndGasEmissionModalOpen] = useState(false);
  const [selectedIndGasEmission, setSelectedIndGasEmission] = useState(null);

  const [gasEmission, setGasEmission] = useState([]);
  const [totalgasEmission, setTotalGasEmission] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Enhanced filters state
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    sortBy: 'created_date',
    sortOrder: 'ASC',
    fuelType: '',
    year: '',
    unit: ''
  });

  // Filter options (These would be populated from API or defined statically)
  const [filterOptions, setFilterOptions] = useState({
    fuelTypes: [],
    years: [],
    units: []
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [IndGastoDelete, setIndGastoDelete] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');

  const handleEdit = (emission) => {
    setIsAddUpdateIndGasEmissionModalOpen(true);
    setSelectedIndGasEmission(emission);
  };

  // Get filter options separately from main data fetch
  const getFilterOptions = async () => {
    try {
      // Ideally, you would have a dedicated API endpoint for this
      const response = await api.get('/admin/getIndGasEmissionFilterOptions');

      if (response.data && response.data.status === "200") {
        setFilterOptions({
          fuelTypes: response.data.gasTypes || [],
          years: response.data.years || [],
          units: response.data.units || []
        });
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);

      // Fallback: Extract from current data if API call fails
      if (gasEmission.length > 0) {
        const uniqueFuelTypes = [...new Set(gasEmission.map(item => item.fuel_name))];
        const uniqueYears = [...new Set(gasEmission.map(item => item.valid_year))];
        const uniqueUnits = [...new Set(gasEmission.map(item => item.unit_name))];

        setFilterOptions({
          fuelTypes: uniqueFuelTypes,
          years: uniqueYears,
          units: uniqueUnits
        });
      }
    }
  };

  const getAllGasEmission = async (pageNumber = page, limit = rowsPerPage, filterParams = filters) => {
    try {
      const response = await api.get('/admin/getIndGasEmission', {
        params: {
          page: pageNumber + 1,
          limit,
          search: filterParams.search,
          status: filterParams.status,
          sortBy: filterParams.sortBy,
          sortOrder: filterParams.sortOrder,
          gasType: filterParams.fuelType,
          year: filterParams.year,
          unit: filterParams.unit
        }
      });
      setGasEmission(response.data.data);
      setTotalGasEmission(response.data.total);
    } catch (error) {
      dispatch(showNotification({
        message: 'Failed to fetch Gas Emission List',
        type: 'error',
      }));
    }
  }

  const confirmDelete = async () => {
    setIsSubmitting(true);

    api.delete(`/admin/deleteindgasEmission/${IndGastoDelete.id}`)
      .then(() => {
        dispatch(showNotification({
          message: 'industrial gas Emission deleted successfully',
          type: 'success',
        }));
        getAllGasEmission();
      })
      .catch(() => {
        dispatch(showNotification({
          message: 'Failed to industrial gas Emission',
          type: 'error',
        }));
      })
      .finally(() => {
        setDeleteDialogOpen(false);
        setIsSubmitting(false);
      });
  }

  const handleActivateDeactivate = async (user) => {
    try {
      setIsSubmitting(true);
      const response = await api.post(`/admin/activateDeactivateIndGasEmission/${user.id}`, {
        is_active: !user.is_active
      });
      if (response.data.status == 200) {
        dispatch(showNotification({
          message: user.is_active ? 'Ind gas Emission factor deactivated successfully' : 'Ind gas Emission factor activated successfully',
          type: "success"
        }));
        getAllGasEmission();
      } else {
        dispatch(showNotification({
          message: user.is_active ? 'Failed to deactivate Ind gas Emission factor' : 'Failed to activate Ind gas Emission factor',
          type: "error"
        }));
      }
    } catch (error) {
      console.error('Error activating/deactivating Ind gas Emission factor', error);
      dispatch(showNotification({
        message: 'An error occurred',
        type: "error"
      }));
    } finally {
      setIsSubmitting(false);
    }
  }

  const handleRemove = (id) => {
    setIndGastoDelete(id);
    setDeleteDialogOpen(true);
  };

  // Handle search with debounce
   const debouncedSearch = React.useCallback(
     debounce((value) => {
       setFilters(prev => ({ ...prev, search: value }));
       setPage(0); // Reset to first page when searching
     }, 500),
     []
   );

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle sorting
  const handleSort = (field) => {
    setFilters(prev => ({
      ...prev,
      sortBy: field,
      sortOrder: prev.sortBy === field && prev.sortOrder === 'ASC' ? 'DESC' : 'ASC'
    }));
  };

  // Apply filters
  const applyFilters = () => {
    setPage(0); // Reset to first page when filters change
    getAllGasEmission(0, rowsPerPage, filters);
    setFilterDialogOpen(false);
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      search: '',
      status: '',
      sortBy: 'created_date',
      sortOrder: 'ASC',
      fuelType: '',
      year: '',
      unit: ''
    });
    setPage(0);
    setFilterDialogOpen(false);
  };

  // Handle filter dialog open
  const handleOpenFilterDialog = () => {
    // Ensure filter options are loaded when dialog opens
    if (filterOptions.fuelTypes.length === 0) {
      getFilterOptions();
    }
    setFilterDialogOpen(true);
  };

  useEffect(() => {
    getAllGasEmission();
    getFilterOptions();
  }, [page, rowsPerPage, filters.sortBy, filters.sortOrder, filters.search, 
    filters.status, filters.fuelType, filters.year, filters.unit]);

  const getStatusLabel = (status) => {
    if (status === 'ACTIVE') return 'Active';
    if (status === 'INACTIVE') return 'Inactive';
    return 'All';
  };

  return (
    <>
      <PageHeader title="Industrial Gas Emission">
        <div className="flex justify-end gap-5 items-center mt-5">
          <div className="flex items-center gap-4 flex-1">
            {/* Search Field */}
            <TextField
              size="small"
              placeholder="Search Ind Gas Emission..."
              value={searchInput}
              InputProps={{
                startAdornment: <Search className="w-5 h-5 text-gray-500 mr-2" />,
              }}
              onChange={(e) => {
                const value = e.target.value;
                setSearchInput(value)
                debouncedSearch(value); // Debounce the actual filter update
              }}
              className="min-w-[300px]"
            />
            {/* Filter Button */}
            <CustomButton
              className="bg-blue-600 hover:bg-blue-700 text-white"
              text={<><Filter size={18} /> Filters</>}
              click={handleOpenFilterDialog}
            />

            {/* Sort Dropdown */}
            <FormControl size="small" className="min-w-[200px]">
              <InputLabel>Sort By</InputLabel>
              <Select
                value={`${filters.sortBy}-${filters.sortOrder}`}
                label="Sort By"
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-');
                  setFilters(prev => ({
                    ...prev,
                    sortBy: field,
                    sortOrder: order
                  }));
                }}
              >
                <MenuItem value="created_date-ASC">Date (Oldest First)</MenuItem>
                <MenuItem value="created_date-DESC">Date (Newest First)</MenuItem>
                <MenuItem value="fuel_name-ASC">Gas Name (A-Z)</MenuItem>
                <MenuItem value="fuel_name-DESC">Gas Name (Z-A)</MenuItem>
                <MenuItem value="emission_kgco2e-ASC">Emission (Low to High)</MenuItem>
                <MenuItem value="emission_kgco2e-DESC">Emission (High to Low)</MenuItem>
              </Select>
            </FormControl>

          </div>
          <CustomButton
            className="bg-green-600 hover:bg-green-700 text-white"
            text={<><PlusIcon /> Add Ind Gas Emission</>}
            click={() => setIsAddUpdateIndGasEmissionModalOpen(true)}
          />
        </div>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Gas</TableCell>
                <TableCell>Year</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell>Emission KgCo2e</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {gasEmission?.map((emission) => (
                <TableRow key={emission.id}>
                  <TableCell>{emission.ind_gas_name}</TableCell>
                  <TableCell>{emission.valid_year}</TableCell>
                  <TableCell>{emission.unit_name}</TableCell>
                  <TableCell>{emission.emission_kgco2e} <span className='font-bold'>kgco2e</span></TableCell>
                  <TableCell>
                    <Switch
                      checked={emission.is_active}
                      color='success'
                      onChange={(e) => handleActivateDeactivate(emission)}
                      disabled={isSubmitting}
                    />
                  </TableCell>
                  <TableCell>
                    {emission.created_date ? format(new Date(emission.created_date), "dd-MM-yyyy") : ""}
                  </TableCell>
                  <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(emission)}
                      sx={{
                        backgroundColor: '#16A34A',
                        color: 'white',
                        '&:hover': { backgroundColor: '#00bf46' },
                        p: '8px',
                      }}
                    >
                      <PenBoxIcon size={20} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleRemove(emission)}
                      sx={{
                        backgroundColor: '#df3e3e',
                        color: 'white',
                        '&:hover': { backgroundColor: '#ff0000' },
                        p: '8px',
                      }}
                    >
                      <DeleteIcon size={20} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  colSpan={7}
                  count={totalgasEmission}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelDisplayedRows={({ from, to, count }) =>
                    `Page ${page + 1} of ${Math.ceil(count / rowsPerPage)} | ${from}-${to} of ${count}`
                  }
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>

        {/* ADD EDIT DIALOG */}
        <Dialog
          open={isAddUpdateIndGasEmissionModalOpen}
          onClose={() => { setIsAddUpdateIndGasEmissionModalOpen(false); setSelectedIndGasEmission(null) }}
          maxWidth="sm"
          fullWidth
        >
          <div className="flex justify-between items-start pt-2 px-2">
            <DialogTitle className="text-lg font-bold mt-1">
              {selectedIndGasEmission ? "Edit Ind Gas Emission" : "Add Ind Gas Emission"}
            </DialogTitle>
            <X
              className="w-6 h-6 cursor-pointer text-gray-500 hover:text-gray-700 transition"
              onClick={() => { setIsAddUpdateIndGasEmissionModalOpen(false); setSelectedIndGasEmission(null) }}
            />
          </div>
          <DialogContent >
            <AddUpdateIndGasEmissionModal
              refetch={getAllGasEmission}
              onClose={() => {
                setIsAddUpdateIndGasEmissionModalOpen(false);
                setSelectedIndGasEmission(null);
              }}
              countryId={selectedIndGasEmission?.id}
              initialData={selectedIndGasEmission}
            />
          </DialogContent>
        </Dialog>

        {/* DELETE DIALOG */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete the fuel emission for {IndGastoDelete?.ind_gas_name} year {IndGastoDelete?.valid_year}? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <div className="flex justify-end gap-5 items-center mt-5">
              <CustomButton
                className="bg-gray-500 hover:bg-gray-700 text-white"
                text="Cancel"
                disabled={isSubmitting}
                click={() => setDeleteDialogOpen(false)}
              />
              <CustomButton
                className="bg-red-600 hover:bg-red-700 text-white"
                click={confirmDelete}
                text='Delete'
                disabled={isSubmitting}
              />
            </div>
          </DialogActions>
        </Dialog>

        {/* Filters Dialog */}
        <Dialog
          open={filterDialogOpen}
          onClose={() => setFilterDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <div className="flex justify-between items-start pt-2 px-2">
            <DialogTitle className="text-lg font-bold mt-1">
              Filter Gas Emissions
            </DialogTitle>
            <X
              className="w-6 h-6 cursor-pointer text-gray-500 hover:text-gray-700 transition"
              onClick={() => setFilterDialogOpen(false)}
            />
          </div>
          <DialogContent>
            {filterOptions.fuelTypes.length === 0 && (
              <div className="text-center py-2">Loading filter options...</div>
            )}
            <div className="space-y-4 py-2">
              {/* Gas Type Filter */}
              <FormControl fullWidth size="small">
                <InputLabel>Gas Type</InputLabel>
                <Select
                  value={filters.fuelType}
                  label="Gas Type"
                  onChange={(e) => setFilters(prev => ({ ...prev, fuelType: e.target.value }))}
                >
                  <MenuItem value="">All Gas Types</MenuItem>
                  {filterOptions.fuelTypes.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Year Filter */}
              <FormControl fullWidth size="small">
                <InputLabel>Year</InputLabel>
                <Select
                  value={filters.year}
                  label="Year"
                  onChange={(e) => setFilters(prev => ({ ...prev, year: e.target.value }))}
                >
                  <MenuItem value="">All Years</MenuItem>
                  {filterOptions.years.map(year => (
                    <MenuItem key={year} value={year}>{year}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Unit Filter */}
              <FormControl fullWidth size="small">
                <InputLabel>Unit</InputLabel>
                <Select
                  value={filters.unit}
                  label="Unit"
                  onChange={(e) => setFilters(prev => ({ ...prev, unit: e.target.value }))}
                >
                  <MenuItem value="">All Units</MenuItem>
                  {filterOptions.units.map(unit => (
                    <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Status Filter */}
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                </Select>
              </FormControl>
            </div>
          </DialogContent>
          <DialogActions>
            <div className="flex justify-end gap-5 items-center mt-5 p-2">
              <CustomButton
                className="bg-gray-500 hover:bg-gray-700 text-white"
                text="Reset"
                click={resetFilters}
              />
              <CustomButton
                className="bg-blue-600 hover:bg-blue-700 text-white"
                text="Apply Filters"
                click={applyFilters}
              />
            </div>
          </DialogActions>
        </Dialog>
      </PageHeader>
    </>
  )
}

export default EmissionIndGasMaster